import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { validatePassword, validateMatch } from '../../utils/validators';
import { friendlyAuthError } from '../../utils/authErrors';

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all';

type Phase = 'loading' | 'reset-form' | 'verified' | 'done' | 'invalid';

/**
 * Firebase email action handler. Email links land here as
 * `/auth/reset-password?mode=<resetPassword|verifyEmail>&oobCode=<code>`.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyResetCode, confirmPasswordReset, applyActionCode } = useAuth();

  const mode = params.get('mode');
  const oobCode = params.get('oobCode');

  const [phase, setPhase] = useState<Phase>('loading');
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!oobCode) {
      setPhase('invalid');
      return;
    }
    if (mode === 'verifyEmail') {
      applyActionCode(oobCode)
        .then(() => setPhase('verified'))
        .catch((e) => {
          setError(friendlyAuthError(e));
          setPhase('invalid');
        });
    } else {
      // Default to the password-reset flow.
      verifyResetCode(oobCode)
        .then((email) => {
          setAccountEmail(email);
          setPhase('reset-form');
        })
        .catch((e) => {
          setError(friendlyAuthError(e));
          setPhase('invalid');
        });
    }
  }, [oobCode, mode, applyActionCode, verifyResetCode]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const err = validatePassword(password) ?? validateMatch(password, confirm);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset(oobCode as string, password);
      setPhase('done');
      setTimeout(() => navigate('/auth/login'), 1800);
    } catch (e2) {
      setError(friendlyAuthError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'loading') {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-neutral-500 dark:text-neutral-400">
        <Loader2 className="h-7 w-7 animate-spin" />
        <p className="text-sm">Verifying link…</p>
      </div>
    );
  }

  if (phase === 'invalid') {
    return (
      <div className="space-y-4 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <AlertTriangle className="h-7 w-7 text-amber-600 dark:text-amber-400" />
        </div>
        <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Link expired or invalid</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{error ?? 'This link is no longer valid. Please request a new one.'}</p>
        <Link to="/auth/forgot-password" className="block text-sm font-medium text-neutral-900 dark:text-neutral-50 hover:underline">
          Request a new link
        </Link>
      </div>
    );
  }

  if (phase === 'verified') {
    return (
      <div className="space-y-4 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
          <CheckCircle2 className="h-7 w-7 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Email verified 🎉</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Your email address has been confirmed. You now have full access.</p>
        <Link
          to="/app"
          className="inline-block w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20"
        >
          Go to dashboard
        </Link>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="space-y-4 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
          <CheckCircle2 className="h-7 w-7 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Password updated</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Redirecting you to sign in…</p>
      </div>
    );
  }

  // reset-form
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Choose a new password</h2>
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
          Resetting the password for <span className="font-medium text-neutral-700">{accountEmail}</span>.
        </p>
      </div>

      <form className="space-y-6" onSubmit={submit}>
        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-700">New password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-700">Confirm new password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
            className={inputClass}
          />
        </div>

        {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? 'Updating…' : 'Reset password'}
        </button>
      </form>
    </div>
  );
}
